import axios from 'axios';
import firebase from 'firebase/compat/app';
import Handlebars from 'handlebars';

import {IQuestionAndPrompts} from '@chancer/common/lib/core/state/model/CompetitionModel';
import {
  TFirebaseComp,
  TFirebaseQuestion,
} from '@chancer/common/lib/interfaces/firestore/FirestoreClientInterfaces';
import {
  CompQuestionTemplateType,
  CompQuestionType,
  CompStatus,
  LeaderboardType,
  QuestionStatus,
  TAiPrompt,
  TComp,
  TCompAnswer,
  TCompCounts,
  TCompPrivate,
  TCompQuestion,
  TCompStatusUpdated,
  TLeaderboard,
  TLeaders,
  TLiveScorePlayer,
  TMediaEntry,
} from '@chancer/common/lib/interfaces/firestore/FirestoreInterfaces';
import {
  TMatch,
  TMatchPlayer,
} from '@chancer/common/lib/interfaces/firestore/LeaguesInterfaces';
import {
  TCaptionPrediction,
  TDynamicQuestionPrediction,
  TMediaRankingPrediction,
  TPrediction,
  TTrivia,
  TTriviaPrediction,
} from '@chancer/common/lib/interfaces/firestore/PredictionInterfaces';
import {convert} from '@chancer/common/lib/utils/FirebaseUtils';
import log from '@chancer/common/lib/utils/Log';
import {allocateTalkingPoints} from '@chancer/common/lib/utils/TalkingPointAllocator';
import {assertType, cleanCast} from '@chancer/common/lib/utils/TypeUtils';
import {useEffect, useState} from 'react';
import {useRecoilValue} from 'recoil';
import {
  captionAiPromptsState,
  competitionLeaderboardsSubscriptionState,
  competitionPrivateSubscriptionState,
  competitionPromptSubscriptionState,
  leaderboardSubscriptionState,
  newCompetitionQuestionsSubscriptionState,
  questionAiPromptsState,
} from '../recoil/FirestoreRecoil';
import {getSessionFromCookie} from '../utils/CookieUtils';
import {
  addCompetitionQuestions,
  getRecentTriviaQuestions,
} from '../utils/FirestoreUtils';
import {isSupported} from '../utils/LiveScoreConfigurationUtils';
import {applyMediaToLink} from '../utils/QuestionUtils';
import {uploadAssetForComp} from './views/Competitions/CompetitionUploadUtils';
import {uploadAssetsForLeaderboard} from './views/Competitions/LeaderboardUploader';

Handlebars.registerHelper('helperMissing', (context) => {
  if (context) {
    return '{{' + context.name + '}}';
  } else {
    return '';
  }
});

const logAiResult = (
  label: string,
  meta: TPrediction,
  output: Record<string, unknown>,
) => {
  const tokens = `${meta.promptTokens} in / ${meta.predictionTokens} out`;
  const time = `${(meta.executionTime / 1000).toFixed(1)}s`;
  console.groupCollapsed(
    `%c[AI] ${label}%c  ${tokens}  ${time}`,
    'font-weight:bold;color:#6366f1',
    'font-weight:normal;color:inherit',
  );
  console.log('%cPrompt', 'font-weight:bold', meta.prompt);
  console.log('%cOutput', 'font-weight:bold', output);
  if (meta.reasoning) {
    console.log('%cReasoning', 'font-weight:bold', meta.reasoning);
  }
  console.log('%cModel', 'font-weight:bold', meta.model, `| temp ${meta.temperature} | topP ${meta.topP}`);
  console.groupEnd();
};

const aiSummary = (meta: TPrediction) =>
  `${meta.promptTokens} in / ${meta.predictionTokens} out, ${(meta.executionTime / 1000).toFixed(1)}s`;

Handlebars.registerHelper('toLowerCase', (str) => {
  return str.toLowerCase();
});

Handlebars.registerHelper('toUpperCase', (str) => {
  return str.toUpperCase();
});

Handlebars.registerHelper('replace', (r1, r2, str) => {
  return str.replace(r1, r2);
});

export const createNewCompetition = async (options: {
  comp: TComp;
  compPrivate: TCompPrivate;
  compPrompt: TAiPrompt | null;
  leaderboard: TLeaderboard;
  questions: TCompQuestion[];
  questionsAndPrompts: IQuestionAndPrompts[];
  config: {
    protocol: string;
    adminOrigin: string;
    adminDomainBypassHosting: string;
    cdnDomain: string;
  };
  tokens?: TMatch;
  files?: File[];
  prefix?: string;
  liveScoreMatchId?: string;
  resetQuestionsStatus?: boolean;
  skipAiGeneration?: boolean;
  logFunction?: (message: string, ...optionalParams: string[]) => void;
}) => {
  let {
    comp,
    compPrivate,
    compPrompt,
    leaderboard,
    questionsAndPrompts,
    config,
    tokens,
    files = [],
    prefix = '',
    resetQuestionsStatus = false,
    skipAiGeneration = false,
    logFunction = console.log,
  } = options;
  let compId: string | undefined;
  const db = firebase.firestore();
  if (comp.template !== true) {
    const compQuerySnap = await db
      .collection('competitions')
      .where('vendor', '==', comp.vendor)
      .where('key', '==', comp.key)
      .limit(1)
      .get();
    if (compQuerySnap.size > 0) {
      throw new Error(
        `Comp already exists with vendor '${comp.vendor}' and key '${comp.key}'`,
      );
    }
  }
  try {
    const compDoc = db.collection('competitions').doc();
    compId = compDoc.id;
    logFunction('Creating competition', compId);
    const queryString = new URLSearchParams({
      id: compDoc.id,
      vendor: comp.vendor,
      key: comp.key,
      title: comp.name,
      description: comp.media.caption ?? '',
    });
    if (comp.template !== true) {
      const shortUrl: {
        shortUrl: string;
        qrCodeUrl: string;
        shortUrlPlay: string;
        qrCodeUrlPlay: string;
      } = (
        await axios.get(
          `${config.adminOrigin}/shrtn?${queryString.toString()}`,
        )
      ).data;
      comp.shortUrl = shortUrl.shortUrl;
      comp.qrCode = shortUrl.qrCodeUrl;
      comp.shortUrlPlay = shortUrl.shortUrlPlay;
      comp.qrCodePlay = shortUrl.qrCodeUrlPlay;
      logFunction('Created shortUrl and qrCode');
    } else {
      comp.shortUrl = '';
      comp.qrCode = '';
    }
    const statusDoc = compDoc.collection('comp_data').doc('status');
    const countsDoc = compDoc.collection('comp_data').doc('counts');
    const privateDoc = compDoc.collection('comp_data').doc('private');
    const promptDoc = compDoc.collection('comp_data').doc('prompt');
    const leaderboardDoc = db.collection('leaderboards').doc(compDoc.id);
    const leadersDoc = leaderboardDoc
      .collection('leaderboard_data')
      .doc('leaders');

    leaderboard.comps = [compDoc.id];
    comp.leaderboards = [compDoc.id, ...comp.leaderboards];
    comp.created = firebase.firestore.FieldValue.serverTimestamp() as any;
    comp.modified = firebase.firestore.FieldValue.serverTimestamp() as any;

    const status: TCompStatusUpdated = {
      status: CompStatus.DRAFT,
      updated: firebase.firestore.FieldValue.serverTimestamp() as any,
    };
    const counts: TCompCounts = {
      numQuestions: questionsAndPrompts.length,
      answersBreakdown: {},
      likesBreakdown: {},
      entriesCount: 0,
      resolvedQuestions: [],
      resolutions: [],
    };
    const leaders: TLeaders = {
      leaders: [],
    };

    if (compPrivate.liveScoreConfiguration) {
      if (tokens) {
        compPrivate.liveScoreConfiguration.scheduleMatchId = tokens.id;
        compPrivate.liveScoreConfiguration.scheduleLeagueId = tokens.leagueId;
      }
      if (options.liveScoreMatchId) {
        compPrivate.liveScoreConfiguration.matchId = options.liveScoreMatchId;
      }
    }

    // Skip processing questions if we don't have tokens
    if (tokens) {
      // We've stubbed in default players when the actual players aren't available
      // so that we can still create competitions and update the player data later
      // when it becomes available. It will be obvious when looking at the answers
      // but we also want to log a warning while this workaround is in place.
      const hasPlayers =
        tokens.homeTeam.players.length > 0 &&
        tokens.awayTeam.players.length > 0 &&
        !tokens.homeTeam.players.some(
          (p) => p.id.startsWith('hp') || p.id.startsWith('ap'),
        );
      const whenNoPlayers = (q: TCompQuestion, message: string) => {
        if (!hasPlayers) {
          logFunction(message);
          q.status = QuestionStatus.DRAFT;
        }
      };
      // Fetched once, before the map, because the map is synchronous.
      const teamAliases = await fetchTeamAliases(comp.teamId);
      questionsAndPrompts = questionsAndPrompts.map((questionAndPrompts) => {
        let q = questionAndPrompts.question;
        let cp = questionAndPrompts.captionPrompt;
        let qp = questionAndPrompts.questionPrompt;
        if (
          !isSupported(
            compPrivate.liveScoreConfiguration?.provider,
            q.templateType,
          )
        ) {
          logFunction(
            `⚠️ Provider '${compPrivate.liveScoreConfiguration?.provider}' does not support questions with '${q.templateType}' template type`,
          );
          q.status = QuestionStatus.DRAFT;
        }
        if (
          q.templateType === CompQuestionTemplateType.WINNER ||
          q.templateType === CompQuestionTemplateType.WINNER_WITH_DRAW
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const {question, compPrivate: updatedCompPrivate} =
            processWinnerQuestion(q, tokens, compPrivate);
          compPrivate = updatedCompPrivate;
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (
          q.templateType === CompQuestionTemplateType.WINNER_LOW_SCORE ||
          q.templateType === CompQuestionTemplateType.WINNER_LOW_SCORE_WITH_DRAW
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const {question, compPrivate: updatedCompPrivate} =
            processWinnerLowScoreQuestion(q, tokens, compPrivate);
          compPrivate = updatedCompPrivate;
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (
          q.templateType === CompQuestionTemplateType.TOTAL_GOALS_BANDS
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const bands = compPrivate.liveScoreConfiguration?.totalBands ?? [2, 4];
          const {question, compPrivate: updatedCompPrivate} =
            processTotalGoalsBandsQuestion(q, bands, compPrivate);
          compPrivate = updatedCompPrivate;
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (
          q.templateType === CompQuestionTemplateType.WINNER_MARGIN ||
          q.templateType === CompQuestionTemplateType.WINNER_MARGIN_WITH_DRAW
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const {question, compPrivate: updatedCompPrivate} =
            processWinnerMarginQuestion(q, tokens, compPrivate);
          compPrivate = updatedCompPrivate;
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (
          q.templateType === CompQuestionTemplateType.FIRST_TO_SCORE ||
          q.templateType === CompQuestionTemplateType.FIRST_TO_SCORE_WITH_NONE
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const {question, compPrivate: updatedCompPrivate} =
            processFirstToScoreQuestion(
              q,
              tokens,
              compPrivate,
              q.templateType ===
                CompQuestionTemplateType.FIRST_TO_SCORE_WITH_NONE,
            );
          compPrivate = updatedCompPrivate;
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (
          q.templateType === CompQuestionTemplateType.FIRST_TO_SCORE_X ||
          q.templateType === CompQuestionTemplateType.FIRST_TO_SCORE_X_WITH_NONE
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const {question, compPrivate: updatedCompPrivate} =
            processFirstToScoreXQuestion(
              q,
              tokens,
              compPrivate,
              q.templateType ===
                CompQuestionTemplateType.FIRST_TO_SCORE_X_WITH_NONE,
            );
          compPrivate = updatedCompPrivate;
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (
          q.templateType === CompQuestionTemplateType.AVERAGE_HIGH_SCORER ||
          q.templateType === CompQuestionTemplateType.LAST_HIGH_SCORER
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const {question, compPrivate: updatedCompPrivate} =
            processHighScorerQuestion(q, tokens, compPrivate);
          compPrivate = updatedCompPrivate;
          whenNoPlayers(
            q,
            '⚠️ No players found for high scorer question, using default players',
          );
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (
          q.templateType ===
            CompQuestionTemplateType.HOME_ABOVE_AVERAGE_SCORERS ||
          q.templateType === CompQuestionTemplateType.AWAY_ABOVE_AVERAGE_SCORERS
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const {question, compPrivate: updatedCompPrivate} =
            processAboveAverageScorersQuestion(q, tokens, compPrivate);
          compPrivate = updatedCompPrivate;
          whenNoPlayers(
            q,
            '⚠️ No players found for above average scorers question, using default players',
          );
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (q.templateType === CompQuestionTemplateType.FAN_MVP) {
          logFunction(`Processing ${q.templateType} question`);
          const {question, compPrivate: updatedCompPrivate} =
            processFanMvpQuestion(q, tokens, compPrivate);
          compPrivate = updatedCompPrivate;
          whenNoPlayers(
            q,
            '⚠️ No players found for fan MVP question, using default players',
          );
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (q.templateType === CompQuestionTemplateType.STATS_SIMPLE) {
          logFunction(`Processing ${q.templateType} question`);
          const {question, compPrivate: updatedCompPrivate} =
            processStatsSimpleQuestion(q, tokens, compPrivate);
          compPrivate = updatedCompPrivate;
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (
          q.templateType === CompQuestionTemplateType.PERIOD_SCORES ||
          q.templateType === CompQuestionTemplateType.PERIOD_SCORES_3Q_EXCL ||
          q.templateType === CompQuestionTemplateType.PERIOD_SCORES_HOME ||
          q.templateType === CompQuestionTemplateType.PERIOD_SCORES_AWAY
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const {question, compPrivate: updatedCompPrivate} =
            processPeriodScoresQuestion(q, tokens, compPrivate, q.templateType);
          compPrivate = updatedCompPrivate;
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (
          q.templateType === CompQuestionTemplateType.HALF_SCORES ||
          q.templateType === CompQuestionTemplateType.HALF_SCORES_DRAW ||
          q.templateType === CompQuestionTemplateType.HALF_SCORES_HOME ||
          q.templateType === CompQuestionTemplateType.HALF_SCORES_AWAY ||
          q.templateType === CompQuestionTemplateType.HALF_SCORES_AWAY_DRAW ||
          q.templateType === CompQuestionTemplateType.HALF_SCORES_HOME_DRAW
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const {question, compPrivate: updatedCompPrivate} =
            processHalfScoresQuestion(q, tokens, compPrivate, q.templateType);
          compPrivate = updatedCompPrivate;
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (
          q.templateType === CompQuestionTemplateType.HOME_HIGH_SCORER ||
          q.templateType === CompQuestionTemplateType.AWAY_HIGH_SCORER
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const {question, compPrivate: updatedCompPrivate} =
            processTeamHighScorerQuestion(q, tokens, compPrivate);
          compPrivate = updatedCompPrivate;
          whenNoPlayers(
            q,
            '⚠️ No players found for team high scorer question, using default players',
          );
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (
          q.templateType === CompQuestionTemplateType.HOME_TRY_SCORER ||
          q.templateType === CompQuestionTemplateType.AWAY_TRY_SCORER
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const isHomeTryScorer =
            q.templateType === CompQuestionTemplateType.HOME_TRY_SCORER;
          const tryTeamPlayers = isHomeTryScorer
            ? tokens.homeTeam.players
            : tokens.awayTeam.players;
          const hasTryPlayers =
            tryTeamPlayers.length > 0 &&
            !tryTeamPlayers.some(
              (p) => p.id.startsWith('hp') || p.id.startsWith('ap'),
            );
          if (!hasTryPlayers) {
            logFunction(
              '⚠️ No players found for try scorer question, using default players',
            );
            q.status = QuestionStatus.DRAFT;
          }
          const {question, compPrivate: updatedCompPrivate} =
            processTryScorerQuestion(q, tokens, compPrivate);
          compPrivate = updatedCompPrivate;
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (
          q.templateType === CompQuestionTemplateType.TEAM_TRY_SCORER ||
          q.templateType === CompQuestionTemplateType.TEAM_GOAL_SCORER
        ) {
          logFunction(`Processing ${q.templateType} question`);
          const side = resolveTeamSide(
            comp.teamId,
            tokens.homeTeam,
            tokens.awayTeam,
            teamAliases,
          );
          if (!side) {
            // Guessing here is how an All Blacks question would end up listing
            // Springboks. DRAFT keeps it out of the game and in front of an
            // operator, and DRAFT questions cannot strand a competition.
            logFunction(
              `⚠️ Could not tell which side "${comp.teamId}" is on for ${tokens.homeTeam.name} v ${tokens.awayTeam.name} — leaving ${q.templateType} question as DRAFT`,
            );
            q.status = QuestionStatus.DRAFT;
          }
          const teamIsHome = side === 'home';
          const teamTryPlayers = teamIsHome
            ? tokens.homeTeam.players
            : tokens.awayTeam.players;
          const hasTeamTryPlayers =
            teamTryPlayers.length > 0 &&
            !teamTryPlayers.some(
              (p) => p.id.startsWith('hp') || p.id.startsWith('ap'),
            );
          if (!hasTeamTryPlayers) {
            logFunction(
              `⚠️ No players found for ${q.templateType} question, using default players`,
            );
            q.status = QuestionStatus.DRAFT;
          }
          const {question, compPrivate: updatedCompPrivate} =
            processTryScorerQuestion(q, tokens, compPrivate, teamIsHome);
          compPrivate = updatedCompPrivate;
          return {question, captionPrompt: cp, questionPrompt: qp};
        } else if (q.templateType === CompQuestionTemplateType.DYNAMIC) {
          logFunction(`Processing ${q.templateType} question (no-op)`);
          return {question: q, captionPrompt: cp, questionPrompt: qp};
        } else {
          return {question: q, captionPrompt: cp, questionPrompt: qp};
        }
      });
    }

    const mapfunc = (
      x:
        | string
        | number
        | boolean
        | any[]
        | Date
        | Record<string, any>
        | null
        | undefined,
    ) => (typeof x === 'string' && tokens ? Handlebars.compile(x)(tokens) : x);

    if (tokens) {
      logFunction('Replacing template tokens');
    }
    comp = mapRecursive({...comp}, mapfunc);
    compPrivate = mapRecursive({...compPrivate}, mapfunc);
    if (compPrompt) {
      compPrompt = mapRecursive({...compPrompt}, mapfunc);
    }
    leaderboard = mapRecursive({...leaderboard}, mapfunc);
    questionsAndPrompts = mapRecursive([...questionsAndPrompts], mapfunc);
    const questionsAndPromptsWithoutFanMvp = questionsAndPrompts.filter(
      (q) => q.question.templateType !== CompQuestionTemplateType.FAN_MVP,
    );

    // Attempt to assign new media to the questions and generate
    // contextual captions
    if (
      comp.tags &&
      comp.tags.length > 0 &&
      comp.template !== true &&
      !skipAiGeneration
    ) {
      logFunction('Getting ranked media');
      let mediaListPrediction: TMediaRankingPrediction | undefined;
      let media: TMediaRankingPrediction['media'] | undefined;
      try {
        const mediaListResponse = await axios.post(
          `${config.protocol}//${config.adminDomainBypassHosting}/getRankedMediaForCaption?__session=${getSessionFromCookie()}`,
          {
            questions: questionsAndPromptsWithoutFanMvp.map((q) => q.question),
            comp: {...comp, id: compDoc.id},
            compPrompt,
            limit: 25,
          },
        );
        mediaListPrediction = cleanCast(
          TMediaRankingPrediction,
          mediaListResponse.data,
        );
        logAiResult('Media Ranking', mediaListPrediction.prediction, {
          media: mediaListPrediction.media.map((m) => `${m.id}: ${m.title}`),
          talkingPoints: mediaListPrediction.talkingPoints ?? null,
          structuredTalkingPoints: mediaListPrediction.structuredTalkingPoints?.length ?? 0,
        });
        logFunction(
          `  → Ranked ${mediaListPrediction.media.length} articles (${aiSummary(mediaListPrediction.prediction)})`,
        );

        media = cleanCast(
          TMediaRankingPrediction,
          mediaListResponse.data,
        ).media;
      } catch (e) {
        logFunction(`Error getting ranked media for caption: ${e}`);
      }

      if (media !== undefined) {
        const isTalkingPointsModel = comp.buildModel === 'talkingPoints';
        const tpAllocations =
          isTalkingPointsModel && mediaListPrediction?.structuredTalkingPoints?.length
            ? allocateTalkingPoints(
                mediaListPrediction.structuredTalkingPoints,
                questionsAndPromptsWithoutFanMvp.map((q) => q.question),
              )
            : [];
        const tpByIndex = new Map(tpAllocations.map((a) => [a.questionIndex, a]));

        const previousCaptions: string[] = [];
        const previousNames: string[] = [];
        const previousOpponent = (() => {
          const tp = mediaListPrediction?.talkingPoints;
          if (!tp) { return undefined; }
          const m = tp.match(
            /(?:loss to|beat|defeated|against|vs\.?)\s+(?:the\s+)?([A-Z][a-zA-Z\s]+?)(?:\s+in\s+Round|\s+\d|\.|,|$)/i,
          );
          return m?.[1]?.trim() ?? undefined;
        })();
        const previousTriviaQuestions: TTrivia[] = [];
        const previousDynamicQuestions: string[] = [];
        const recentTriviaQuestions = await getRecentTriviaQuestions(
          comp.vendor,
        );
        const usedArticleIds = new Set<string>();
        for (
          let i = 0;
          i < questionsAndPromptsWithoutFanMvp.length && i < media.length;
          i++
        ) {
          if (i > 0) {
            await new Promise((resolve) => setTimeout(resolve, 5000));
          }
          logFunction(`Assigning media to question ${i + 1}`);
          const questionAndPrompts = questionsAndPromptsWithoutFanMvp[i];
          const tpAlloc = tpByIndex.get(i);
          const findNextUnused = (startIndex: number) => {
            for (let j = startIndex; j < media.length; j++) {
              if (!usedArticleIds.has(media[j].id)) { return media[j]; }
            }
            for (let j = 0; j < startIndex; j++) {
              if (!usedArticleIds.has(media[j].id)) { return media[j]; }
            }
            return media[startIndex];
          };
          const m = tpAlloc
            ? (media.find((x) => x.id === tpAlloc.articleId && !usedArticleIds.has(x.id)) ?? findNextUnused(i))
            : findNextUnused(i);
          usedArticleIds.add(m.id);
          const talkingPointContext = tpAlloc
            ? `${tpAlloc.talkingPoint.title}\n${tpAlloc.talkingPoint.summary}`
            : undefined;
          questionAndPrompts.question.media = assertType<TMediaEntry>({
            ...(m.socialEmbed
              ? {social: {embed: m.socialEmbed}}
              : {
                  image: {
                    url: m.imagePortrait!,
                  },
                }),
          });
          questionAndPrompts.question.link = applyMediaToLink(
            questionAndPrompts.question.link,
            m,
          );
          // Extract accent colour from the question image
          if (m.imagePortrait) {
            try {
              const accentResponse = await axios.post(
                `${config.protocol}//${config.adminDomainBypassHosting}/accent-color?__session=${getSessionFromCookie()}`,
                {imageUrl: m.imagePortrait},
              );
              const color = accentResponse.data?.accentColor as string | null;
              if (color) {
                questionAndPrompts.question.accentColor = color;
              }
            } catch {/* non-fatal */}
          }

          // Generate question, answers and correct answer for trivia
          if (questionAndPrompts.question.type === CompQuestionType.TRIVIA) {
            try {
              const response = await axios.post(
                `${config.protocol}//${config.adminDomainBypassHosting}/generateTriviaQuestion?__session=${getSessionFromCookie()}`,
                {
                  mediaId: m.id,
                  mediaIds: media
                    .filter((mm) => mm.id !== m.id)
                    .map((mm) => mm.id),
                  comp: {...comp, id: compDoc.id},
                  compPrompt,
                  prompt: questionAndPrompts.questionPrompt?.prompt,
                  previousTriviaQuestions,
                  recentTriviaQuestions,
                  previousNames,
                  talkingPointContext,
                },
              );
              const prediction = cleanCast(TTriviaPrediction, response.data);
              logAiResult(`Trivia Q${i + 1}`, prediction.prediction, {
                trivia: prediction.trivia,
              });
              logFunction(
                `  → Trivia Q${i + 1}: "${prediction.trivia?.question ?? '(empty)'}" (${aiSummary(prediction.prediction)})`,
              );
              if (prediction.trivia) {
                questionAndPrompts.question.question =
                  prediction.trivia.question;
                questionAndPrompts.question.answers =
                  prediction.trivia.answers.map((a) =>
                    assertType<TCompAnswer>({label: a}),
                  );
                questionAndPrompts.resolved =
                  prediction.trivia.correctAnswerIndex;
                previousTriviaQuestions.push(prediction.trivia);
              }
            } catch (e) {
              logFunction(
                `Error generating trivia for question ${i + 1}: ${e}`,
              );
              logFunction(
                `  ⚠️ Q${i + 1} left as DRAFT — it still has its template question and answers`,
              );
              questionAndPrompts.question.status = QuestionStatus.DRAFT;
            }
          }

          // Generate question, answers and resolution rule for dynamic questions
          if (
            questionAndPrompts.question.templateType ===
            CompQuestionTemplateType.DYNAMIC
          ) {
            try {
              const homeTeamName =
                tokens?.homeTeam?.shortName ||
                tokens?.homeTeam?.name ||
                '';
              const awayTeamName =
                tokens?.awayTeam?.shortName ||
                tokens?.awayTeam?.name ||
                '';
              logFunction(
                `Generating dynamic question ${i + 1} (${homeTeamName} vs ${awayTeamName})`,
              );
              const response = await axios.post(
                `${config.protocol}//${config.adminDomainBypassHosting}/generateDynamicQuestion?__session=${getSessionFromCookie()}`,
                {
                  comp: {...comp, id: compDoc.id},
                  compPrompt,
                  prompt: questionAndPrompts.questionPrompt?.prompt,
                  homeTeamName,
                  awayTeamName,
                  liveScoreProvider:
                    compPrivate.liveScoreConfiguration?.provider,
                  previousQuestions: previousDynamicQuestions,
                  talkingPointContext,
                  mediaId: m.id,
                },
              );
              const prediction = response.data
                .prediction as TDynamicQuestionPrediction;
              const meta = response.data.meta as TPrediction;
              logAiResult(`Dynamic Q${i + 1}`, meta, {
                question: prediction.question,
                answers: prediction.answers,
                resolutionRule: prediction.resolutionRule,
                rationale: prediction.rationale,
              });
              logFunction(
                `  → Dynamic Q${i + 1}: "${prediction.question}" (${aiSummary(meta)})`,
              );
              if (prediction) {
                questionAndPrompts.question.question =
                  prediction.question;
                questionAndPrompts.question.answers =
                  prediction.answers.map((a) =>
                    assertType<TCompAnswer>({label: a}),
                  );
                questionAndPrompts.question.resolutionRule =
                  prediction.resolutionRule;
                previousDynamicQuestions.push(prediction.question);
              }
            } catch (e) {
              // Continuing here is what published "Legendary players" with
              // answers "Answer A"/"Answer B" and no resolutionRule: the
              // question kept whatever the cloned template carried. A dynamic
              // question with no rule can never be graded, and an ungradeable
              // question holds the whole competition in INPLAY — so it must not
              // reach players. DRAFT is excluded from the RESULTED gate.
              logFunction(
                `Error generating dynamic question ${i + 1}: ${e}`,
              );
              logFunction(
                `  ⚠️ Q${i + 1} left as DRAFT — it still has its template text and no resolution rule`,
              );
              questionAndPrompts.question.status = QuestionStatus.DRAFT;
            }
          }

          await new Promise((resolve) => setTimeout(resolve, 3000));
          try {
            const response = await axios.post(
              `${config.adminOrigin}/generateCaption`,
              {
                mediaId: m.id,
                mediaIds: media
                  .filter((mm) => mm.id !== m.id)
                  .map((mm) => mm.id),
                question: questionAndPrompts,
                comp: {...comp, id: compDoc.id},
                compPrompt,
                previousCaptions,
                previousNames,
                previousOpponent,
                talkingPointContext,
              },
            );
            const prediction = cleanCast(TCaptionPrediction, response.data);
            logAiResult(`Caption Q${i + 1}`, prediction.prediction, {
              caption: prediction.caption,
              name: prediction.name,
            });
            logFunction(
              `  → Caption Q${i + 1}: "${prediction.caption ?? '(empty)'}" (${aiSummary(prediction.prediction)})`,
            );
            if (prediction.caption) {
              previousCaptions.push(prediction.caption);
              questionAndPrompts.question.media.caption = prediction.caption;
            }
            if (prediction.name) {
              previousNames.push(prediction.name);
              questionAndPrompts.question.name = prediction.name;
            }
          } catch (e) {
            // Deliberately not DRAFT. A missing caption degrades presentation;
            // the question is still answerable and still gradeable, so pulling
            // it from the game would cost more than it saves. It keeps the
            // template's caption, which may be placeholder text — the
            // pre-publish check is where that gets caught.
            logFunction(`Error generating caption for question ${i + 1}: ${e}`);
            logFunction(
              `  ⚠️ Q${i + 1} kept its template caption and name — check both before publishing`,
            );
          }
        }
      } else {
        logFunction('Unable to assign media to questions');
      }
    } else {
      if (skipAiGeneration) {
        logFunction(
          'No ai genreration executed as this skipAiGeneration is set to true',
        );
      } else if (comp.template) {
        logFunction('No ai genreration executed as this is a template');
      } else {
        logFunction('No tags found to assign media to questions');
      }
    }

    logFunction('Uploading leaderboard assets');
    await uploadAssetsForLeaderboard(
      leaderboard,
      files,
      config,
      logFunction,
      false,
      prefix,
    );

    logFunction('Uploading competition assets');
    await uploadAssetForComp(
      comp,
      questionsAndPrompts.map((q) => q.question),
      files,
      config,
      logFunction,
      prefix,
    );

    // Auto-set promo from Q1 image and cover from Q2 image, then smart-crop each
    const smartCrop = async (imageUrl: string, type: 'promo' | 'cover') => {
      try {
        const r = await axios.post(
          `${config.protocol}//${config.adminDomainBypassHosting}/smartcrop?__session=${getSessionFromCookie()}`,
          {imageUrl, type},
        );
        return (r.data?.url as string | null) ?? null;
      } catch {
        return null;
      }
    };
    if (questionsAndPrompts[0]?.question?.media?.image?.url) {
      const src = questionsAndPrompts[0].question.media.image.url;
      comp.promo = {image: {url: (await smartCrop(src, 'promo')) ?? src}};
    }
    if (questionsAndPrompts[1]?.question?.media?.image?.url) {
      const src = questionsAndPrompts[1].question.media.image.url;
      comp.media = {image: {url: (await smartCrop(src, 'cover')) ?? src}};
    }

    logFunction('Uploading new documents to database');
    const batch = db.batch();
    batch.set(compDoc, comp);
    batch.set(statusDoc, status);
    batch.set(countsDoc, counts);
    batch.set(privateDoc, compPrivate);
    if (compPrompt && compPrompt.prompt) {
      batch.set(promptDoc, {
        prompt: compPrompt.prompt,
        created: firebase.firestore.FieldValue.serverTimestamp() as any,
      });
    }
    batch.set(leaderboardDoc, leaderboard);
    batch.set(leadersDoc, leaders);
    await addCompetitionQuestions(batch, compDoc.id, questionsAndPrompts, {
      overwriteStatus: resetQuestionsStatus,
    });
    for (let i = 0; i < comp.leaderboards.length; i++) {
      const lbId = comp.leaderboards[i];
      if (lbId !== compDoc.id) {
        const lbRef = db.collection('leaderboards').doc(lbId);
        const lbDoc = await lbRef.get();
        const lb = convert(TLeaderboard, lbDoc);
        batch.update(
          lbRef,
          assertType<Pick<TLeaderboard, 'comps'>>({
            comps: [...lb.comps, compDoc.id],
          }),
        );
      }
    }
    await batch.commit();
    logFunction('Documents created');
  } catch (err) {
    logFunction(JSON.stringify(err));
    throw err;
  }
  return compId;
};

export const useCompToCloneDetails = (
  comp: TFirebaseComp | undefined,
  startTime: Date,
) => {
  const [startTimeOffset, setStartTimeOffset] = useState(0);
  const [newComp, setNewComp] = useState<TComp | null>(null);
  const [newCompPrivate, setNewCompPrivate] = useState<TCompPrivate | null>(
    null,
  );
  const [newCompLeaderboard, setNewCompLeaderboard] =
    useState<TLeaderboard | null>(null);
  const [newCompQuestions, setNewCompQuestions] = useState<
    TCompQuestion[] | null
  >(null);
  const [newCompQuestionsAndPrompts, setNewCompQuestionsAndPrompts] = useState<
    IQuestionAndPrompts[] | null
  >(null);

  const compToClonePrivate = useRecoilValue(
    competitionPrivateSubscriptionState(comp?.id ?? null),
  );
  const compToClonePrompt = useRecoilValue(
    competitionPromptSubscriptionState(comp?.id ?? null),
  );
  const compToCloneLeaderboard = useRecoilValue(
    leaderboardSubscriptionState(comp?.id ?? null),
  );
  const compToCloneMultigameLeaderboards = useRecoilValue(
    competitionLeaderboardsSubscriptionState(comp?.id ?? null),
  )?.filter((l) => l.id !== comp?.id);

  const compToCloneQuestions = useRecoilValue(
    newCompetitionQuestionsSubscriptionState(comp?.id ?? null),
  );
  const compToCloneCaptionPrompts = useRecoilValue(
    captionAiPromptsState(compToCloneQuestions.map((q) => q.id)),
  );
  const compToCloneQuestionPrompts = useRecoilValue(
    questionAiPromptsState(compToCloneQuestions.map((q) => q.id)),
  );

  useEffect(() => {
    if (comp) {
      setStartTimeOffset(startTime.getTime() - comp.starts.toDate().getTime());
      setNewComp((prev) =>
        prev !== null
          ? {
              ...prev,
              starts: firebase.firestore.Timestamp.fromDate(startTime),
              closes: firebase.firestore.Timestamp.fromDate(startTime),
            }
          : {
              ...cleanCast(TComp, comp),
              starts: firebase.firestore.Timestamp.fromDate(startTime),
              closes: firebase.firestore.Timestamp.fromDate(startTime),
              created: firebase.firestore.FieldValue.serverTimestamp() as any,
              sponsor: comp.sponsor ?? {},
              published: false,
              featured: false,
              chat: false,
              shortUrl: '',
              key: `${comp.key}${comp.template === true ? '' : '-copy'}`,
              leaderboards: comp.leaderboards.filter((l) => l !== comp.id),
            },
      );
    }
  }, [comp, startTime]);

  useEffect(() => {
    if (compToClonePrivate) {
      const cleaned = {...compToClonePrivate, notificationsSent: {}};
      delete cleaned.liveScore;
      setNewCompPrivate(cleaned);
    }
  }, [compToClonePrivate]);

  useEffect(() => {
    if (compToCloneLeaderboard) {
      setNewCompLeaderboard({
        ...compToCloneLeaderboard,
        comps: [],
        key: 'ingame',
        type: LeaderboardType.INGAME,
        published: true,
        publishUserLeaders: false,
      });
    }
  }, [compToCloneLeaderboard]);

  useEffect(() => {
    if (compToCloneQuestions) {
      const getCleanQuestion = (q: TCompQuestion, offset: number) => {
        const clone = {
          ...cleanCast(TCompQuestion, q),
          resolved: null,
        };
        if (clone.opens) {
          clone.opens = firebase.firestore.Timestamp.fromDate(
            new Date(clone.opens.toDate().getTime() + startTimeOffset),
          );
        }
        if (clone.starts) {
          clone.starts = firebase.firestore.Timestamp.fromDate(
            new Date(clone.starts.toDate().getTime() + startTimeOffset),
          );
        }
        return clone;
      };
      setNewCompQuestions(
        compToCloneQuestions.map((q) => getCleanQuestion(q, startTimeOffset)),
      );
      setNewCompQuestionsAndPrompts(
        compToCloneQuestions.map((q) => {
          const captionPrompt = compToCloneCaptionPrompts.get(q.id);
          const questionPrompt = compToCloneQuestionPrompts.get(q.id);
          return {
            question: getCleanQuestion(q, startTimeOffset),
            captionPrompt: captionPrompt
              ? cleanCast(TAiPrompt, captionPrompt)
              : null,
            questionPrompt: questionPrompt
              ? cleanCast(TAiPrompt, questionPrompt)
              : null,
          };
        }),
      );
    }
  }, [
    compToCloneQuestions,
    compToCloneQuestionPrompts,
    startTimeOffset,
    compToCloneCaptionPrompts,
  ]);

  return {
    compToClonePrivate,
    compToClonePrompt,
    compToCloneLeaderboard,
    compToCloneMultigameLeaderboards,
    compToCloneQuestions,
    compToCloneCaptionPrompts,
    compToCloneQuestionPrompts,
    newComp,
    setNewComp,
    newCompPrivate,
    setNewCompPrivate,
    newCompLeaderboard,
    setNewCompLeaderboard,
    newCompQuestions,
    setNewCompQuestions,
    newCompQuestionsAndPrompts,
    setNewCompQuestionsAndPrompts,
  };
};

export const mapRecursive = (
  x: object,
  fn: (
    x:
      | string
      | number
      | Date
      | null
      | boolean
      | Record<string, any>
      | Array<any>
      | null
      | undefined,
  ) =>
    | string
    | number
    | Date
    | null
    | boolean
    | Record<string, any>
    | Array<any>
    | null
    | undefined,
): any => {
  if (x && isClass(x)) {
    log.debug('Ignoring class', x);
    return x;
  }
  if (Array.isArray(x)) {
    return x.map((v) => mapRecursive(v, fn));
  }
  if (x && typeof x === 'object') {
    return Object.fromEntries(
      Object.entries(x).map(([k, v]) => [k, mapRecursive(v, fn)]),
    );
  }
  return fn(x);
};

const isClass = (target: any) => {
  return (
    typeof target === 'object' &&
    /^(object|array)$/i.test(target.constructor.name) === false
  );
};

type TeamMarginType = Pick<TMatch['homeTeam'], 'shortName' | 'winningMargin'>;
export const processWinnerMarginQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: {homeTeam: TeamMarginType; awayTeam: TeamMarginType},
  compPrivate: TCompPrivate,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log('Processing winner margin question', question);
  let marginHome = parseInt(tokens.homeTeam?.winningMargin ?? '0', 10) ?? 0;
  let marginAway = parseInt(tokens.awayTeam?.winningMargin ?? '0', 10) ?? 0;
  if (isNaN(marginHome)) {
    marginHome = 0;
  }
  if (isNaN(marginAway)) {
    marginAway = 0;
  }
  console.log('marginHome', marginHome);
  console.log('marginAway', marginAway);

  if (marginHome <= 0 && marginAway <= 0) {
    throw new Error(
      `Either homeTeam.winningMargin or awayTeam.winningMargin is required to be set and a positive value as you have a '${CompQuestionTemplateType.WINNER_MARGIN}' question`,
    );
  }
  if (marginHome > 0 && marginAway > 0) {
    throw new Error(
      `Only one of homeTeam.winningMargin or awayTeam.winningMargin can be positive for a '${CompQuestionTemplateType.WINNER_MARGIN}' question`,
    );
  }
  let margin = marginHome;
  if (marginAway > 0) {
    margin = marginAway * -1;
  }
  if (!compPrivate.liveScoreConfiguration) {
    throw new Error(
      `A liveScoreConfiguration is required on TCompPrivate to configure a '${CompQuestionTemplateType.WINNER_MARGIN}' question`,
    );
  }

  compPrivate = {
    ...compPrivate,
    liveScoreConfiguration: {
      ...compPrivate.liveScoreConfiguration,
      winnerMargin: margin,
    },
  };

  if (margin > 0) {
    question = {
      ...question,
      answers: [
        {label: `${tokens.homeTeam.shortName} by 1-${margin}`},
        {label: `${tokens.homeTeam.shortName} by ${margin + 1}+`},
        {
          label: `${tokens.awayTeam.shortName}${question.templateType === CompQuestionTemplateType.WINNER_MARGIN_WITH_DRAW ? ' (or draw)' : ''}`,
        },
      ],
    };
  } else {
    question = {
      ...question,
      answers: [
        {
          label: `${tokens.homeTeam.shortName}${question.templateType === CompQuestionTemplateType.WINNER_MARGIN_WITH_DRAW ? ' (or draw)' : ''}`,
        },
        {label: `${tokens.awayTeam.shortName} by 1-${margin * -1}`},
        {label: `${tokens.awayTeam.shortName} by ${margin * -1 + 1}+`},
      ],
    };
  }
  return {question, compPrivate};
};

type TeamNameType = Pick<TMatch['homeTeam'], 'shortName'>;
export const processWinnerQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: {homeTeam: TeamNameType; awayTeam: TeamNameType},
  compPrivate: TCompPrivate,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log('Processing winner question', question);
  question = {
    ...question,
    answers: [
      {label: `${tokens.homeTeam.shortName}`},
      {label: `${tokens.awayTeam.shortName}`},
      ...(question.templateType === CompQuestionTemplateType.WINNER_WITH_DRAW
        ? [{label: 'Draw'}]
        : []),
    ],
  };
  return {question, compPrivate};
};

export const processWinnerLowScoreQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: {homeTeam: TeamNameType; awayTeam: TeamNameType},
  compPrivate: TCompPrivate,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log('Processing winner low score question', question);
  question = {
    ...question,
    answers: [
      {label: `${tokens.homeTeam.shortName} by 3+`},
      {label: `${tokens.homeTeam.shortName} by 2`},
      {label: `${tokens.homeTeam.shortName} by 1`},
      ...(question.templateType ===
      CompQuestionTemplateType.WINNER_LOW_SCORE_WITH_DRAW
        ? [{label: 'Draw'}]
        : []),
      {label: `${tokens.awayTeam.shortName} by 1`},
      {label: `${tokens.awayTeam.shortName} by 2`},
      {label: `${tokens.awayTeam.shortName} by 3+`},
    ],
  };
  return {question, compPrivate};
};

export const processTotalGoalsBandsQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  bands: number[],
  compPrivate: TCompPrivate,
): {question: Q; compPrivate: TCompPrivate} => {
  if (!bands.length) {
    throw new Error(
      `At least one threshold is required for a '${CompQuestionTemplateType.TOTAL_GOALS_BANDS}' question`,
    );
  }
  if (!compPrivate.liveScoreConfiguration) {
    throw new Error(
      `A liveScoreConfiguration is required on TCompPrivate to configure a '${CompQuestionTemplateType.TOTAL_GOALS_BANDS}' question`,
    );
  }
  const sorted = [...bands].sort((a, b) => a - b);
  const labels: string[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i === 0) {
      labels.push(`0–${sorted[0] - 1}`);
    } else {
      labels.push(`${sorted[i - 1]}–${sorted[i] - 1}`);
    }
  }
  labels.push(`${sorted[sorted.length - 1]}+`);
  compPrivate = {
    ...compPrivate,
    liveScoreConfiguration: {
      ...compPrivate.liveScoreConfiguration,
      totalBands: sorted,
    },
  };
  question = {
    ...question,
    answers: labels.map((label) => ({label})),
  };
  return {question, compPrivate};
};

export const processFirstToScoreQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: {homeTeam: TeamNameType; awayTeam: TeamNameType},
  compPrivate: TCompPrivate,
  includeNoneOption: boolean = false,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log('Processing first to score question', question);
  question = {
    ...question,
    answers: [
      {label: `${tokens.homeTeam.shortName}`},
      {label: `${tokens.awayTeam.shortName}`},
      ...(includeNoneOption ? [{label: 'No score (0-0)'}] : []),
    ],
  };
  return {question, compPrivate};
};

export const processFirstToScoreXQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: Pick<TMatch, 'firstToScoreX'> & {
    homeTeam: TeamNameType;
    awayTeam: TeamNameType;
  },
  compPrivate: TCompPrivate,
  includeNoneOption: boolean = false,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log('Processing first to score x question', question);
  let firstToScoreX = parseInt(tokens.firstToScoreX ?? '0', 10) ?? 0;
  if (isNaN(firstToScoreX)) {
    firstToScoreX = 0;
  }
  console.log('firstToScoreX', firstToScoreX);

  if (firstToScoreX <= 0) {
    throw new Error(
      `firstToScoreX is required to be set and a positive value as you have a '${question.templateType}' question`,
    );
  }
  if (!compPrivate.liveScoreConfiguration) {
    throw new Error(
      `A liveScoreConfiguration is required on TCompPrivate to configure a '${question.templateType}' question`,
    );
  }
  compPrivate = {
    ...compPrivate,
    liveScoreConfiguration: {
      ...compPrivate.liveScoreConfiguration,
      firstToScoreX: firstToScoreX,
    },
  };
  question = {
    ...question,
    answers: [
      {label: `${tokens.homeTeam.shortName}`},
      {label: `${tokens.awayTeam.shortName}`},
      ...(includeNoneOption ? [{label: 'Neither team'}] : []),
    ],
  };
  return {question, compPrivate};
};

type TeamSimpleStatsType = Pick<
  TMatch['homeTeam'],
  'shortName' | 'teamToScoreX'
>;
export const processStatsSimpleQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: Pick<TMatch, 'totalOverUnder'> & {
    homeTeam: TeamSimpleStatsType;
    awayTeam: TeamSimpleStatsType;
  },
  compPrivate: TCompPrivate,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log('Processing simple stats', question);
  let totalOverUnder = parseInt(tokens.totalOverUnder ?? '0', 10) ?? 0;
  if (isNaN(totalOverUnder)) {
    totalOverUnder = 0;
  }
  if (totalOverUnder <= 0) {
    throw new Error(
      `[totalOverUnder] is required to be set and a positive value as you have a '${CompQuestionTemplateType.STATS_SIMPLE}' question`,
    );
  }
  let homeTeamToScoreX =
    parseInt(tokens.homeTeam?.teamToScoreX ?? '0', 10) ?? 0;
  if (isNaN(homeTeamToScoreX)) {
    homeTeamToScoreX = 0;
  }
  if (homeTeamToScoreX <= 0) {
    throw new Error(
      `[homeTeamToScoreX] is required to be set and a positive value as you have a '${CompQuestionTemplateType.STATS_SIMPLE}' question`,
    );
  }
  let awayTeamToScoreX =
    parseInt(tokens.awayTeam?.teamToScoreX ?? '0', 10) ?? 0;
  if (isNaN(awayTeamToScoreX)) {
    awayTeamToScoreX = 0;
  }
  if (awayTeamToScoreX <= 0) {
    throw new Error(
      `[awayTeamToScoreX] is required to be set and a positive value as you have a '${CompQuestionTemplateType.STATS_SIMPLE}' question`,
    );
  }

  if (!compPrivate.liveScoreConfiguration) {
    throw new Error(
      `A liveScoreConfiguration is required on TCompPrivate to configure a '${CompQuestionTemplateType.STATS_SIMPLE}' question`,
    );
  }
  compPrivate = {
    ...compPrivate,
    liveScoreConfiguration: {
      ...compPrivate.liveScoreConfiguration,
      statsTotalOverUnder: totalOverUnder,
      statsHomeToScoreX: homeTeamToScoreX,
      statsAwayToScoreX: awayTeamToScoreX,
    },
  };
  question = {
    ...question,
    answers: [
      {label: `${tokens.homeTeam.shortName} to score ${homeTeamToScoreX}+`},
      {label: `${tokens.awayTeam.shortName} to score ${awayTeamToScoreX}+`},
      {label: `Total match points ${totalOverUnder}+`},
      {label: `Total match points less than ${totalOverUnder}`},
    ],
  };
  return {question, compPrivate};
};

export const processPeriodScoresQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: {homeTeam: TeamNameType; awayTeam: TeamNameType},
  compPrivate: TCompPrivate,
  template:
    | CompQuestionTemplateType.PERIOD_SCORES
    | CompQuestionTemplateType.PERIOD_SCORES_3Q_EXCL
    | CompQuestionTemplateType.PERIOD_SCORES_HOME
    | CompQuestionTemplateType.PERIOD_SCORES_AWAY,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log(`Processing ${CompQuestionTemplateType.PERIOD_SCORES}`, question);

  if (!compPrivate.liveScoreConfiguration) {
    throw new Error(
      `A liveScoreConfiguration is required on TCompPrivate to configure a '${CompQuestionTemplateType.PERIOD_SCORES}' question`,
    );
  }
  if (template === CompQuestionTemplateType.PERIOD_SCORES) {
    question = {
      ...question,
      answers: [
        {label: `${tokens.homeTeam.shortName} ahead at half time`},
        {label: `${tokens.awayTeam.shortName} ahead at half time`},
        {label: `${tokens.homeTeam.shortName} to score first after half time`},
        {label: `${tokens.awayTeam.shortName} to score first after half time`},
        {label: `${tokens.homeTeam.shortName} to win 3 or more quarters`},
        {label: `${tokens.awayTeam.shortName} to win 3 or more quarters`},
        {label: `${tokens.homeTeam.shortName} win the last quarter`},
        {label: `${tokens.awayTeam.shortName} win the last quarter`},
        {label: 'Team ahead at halftime wins the game'},
      ],
    };
  } else if (template === CompQuestionTemplateType.PERIOD_SCORES_3Q_EXCL) {
    question = {
      ...question,
      answers: [
        {label: `${tokens.homeTeam.shortName} ahead at half time`},
        {label: `${tokens.awayTeam.shortName} ahead at half time`},
        {label: `${tokens.homeTeam.shortName} to score first after half time`},
        {label: `${tokens.awayTeam.shortName} to score first after half time`},
        {label: `${tokens.homeTeam.shortName} win the last quarter`},
        {label: `${tokens.awayTeam.shortName} win the last quarter`},
        {label: 'Team ahead at halftime wins the game'},
      ],
    };
  } else if (
    template === CompQuestionTemplateType.PERIOD_SCORES_HOME ||
    template === CompQuestionTemplateType.PERIOD_SCORES_AWAY
  ) {
    const teamName =
      template === CompQuestionTemplateType.PERIOD_SCORES_HOME
        ? tokens.homeTeam.shortName
        : tokens.awayTeam.shortName;
    question = {
      ...question,
      answers: [
        {label: `${teamName} ahead at half time`},
        {label: `${teamName} to score first after half time`},
        {label: `${teamName} to win 3 or more quarters`},
        {label: `${teamName} win the last quarter`},
        {label: 'Team ahead at halftime wins the game'},
      ],
    };
  }
  return {question, compPrivate};
};

export const processHalfScoresQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: {homeTeam: TeamNameType; awayTeam: TeamNameType},
  compPrivate: TCompPrivate,
  template:
    | CompQuestionTemplateType.HALF_SCORES
    | CompQuestionTemplateType.HALF_SCORES_DRAW
    | CompQuestionTemplateType.HALF_SCORES_HOME
    | CompQuestionTemplateType.HALF_SCORES_AWAY
    | CompQuestionTemplateType.HALF_SCORES_AWAY_DRAW
    | CompQuestionTemplateType.HALF_SCORES_HOME_DRAW,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log(`Processing ${CompQuestionTemplateType.HALF_SCORES}`, question);

  if (!compPrivate.liveScoreConfiguration) {
    throw new Error(
      `A liveScoreConfiguration is required on TCompPrivate to configure a '${CompQuestionTemplateType.HALF_SCORES}' question`,
    );
  }
  if (template === CompQuestionTemplateType.HALF_SCORES) {
    question = {
      ...question,
      answers: [
        {label: `${tokens.homeTeam.shortName} ahead at half time`},
        {label: `${tokens.awayTeam.shortName} ahead at half time`},
        {label: `${tokens.homeTeam.shortName} to score first after half time`},
        {label: `${tokens.awayTeam.shortName} to score first after half time`},
        {label: `${tokens.homeTeam.shortName} win the second half`},
        {label: `${tokens.awayTeam.shortName} win the second half`},
        {label: 'Team ahead at halftime wins the game'},
      ],
    };
  } else if (template === CompQuestionTemplateType.HALF_SCORES_DRAW) {
    question = {
      ...question,
      answers: [
        {label: `${tokens.homeTeam.shortName} ahead at half time`},
        {label: `${tokens.awayTeam.shortName} ahead at half time`},
        {label: 'Draw at half time'},
        {label: `${tokens.homeTeam.shortName} to score first after half time`},
        {label: `${tokens.awayTeam.shortName} to score first after half time`},
        {label: `${tokens.homeTeam.shortName} win the second half`},
        {label: `${tokens.awayTeam.shortName} win the second half`},
        {label: 'Team ahead at halftime wins the game'},
      ],
    };
  } else if (
    template === CompQuestionTemplateType.HALF_SCORES_HOME ||
    template === CompQuestionTemplateType.HALF_SCORES_AWAY
  ) {
    const teamName =
      template === CompQuestionTemplateType.HALF_SCORES_HOME
        ? tokens.homeTeam.shortName
        : tokens.awayTeam.shortName;
    question = {
      ...question,
      answers: [
        {label: `${teamName} ahead at half time`},
        {label: `${teamName} to score first after half time`},
        {label: `${teamName} win the second half`},
        {label: 'Team ahead at halftime wins the game'},
      ],
    };
  } else if (
    template === CompQuestionTemplateType.HALF_SCORES_HOME_DRAW ||
    template === CompQuestionTemplateType.HALF_SCORES_AWAY_DRAW
  ) {
    const teamName =
      template === CompQuestionTemplateType.HALF_SCORES_HOME_DRAW
        ? tokens.homeTeam.shortName
        : tokens.awayTeam.shortName;
    question = {
      ...question,
      answers: [
        {label: `${teamName} ahead at half time`},
        {label: 'Draw at half time'},
        {label: `${teamName} to score first after half time`},
        {label: `${teamName} win the second half`},
        {label: 'Team ahead at halftime wins the game'},
      ],
    };
  }
  return {question, compPrivate};
};

const bracketedScores = (
  player: Pick<TMatchPlayer, 'averageScore' | 'lastScore'>,
) =>
  player.lastScore !== undefined || player.averageScore !== undefined
    ? `(${player.lastScore !== undefined ? `last ${player.lastScore.toFixed(0)}${player.averageScore !== undefined ? ', ' : ''}` : ''}${player.averageScore !== undefined ? `avg ${player.averageScore.toFixed(1)}` : ''})`
    : '';

type HighScorerType = Pick<TMatch['homeTeam'], 'code' | 'players'>;
export const processHighScorerQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: {homeTeam: HighScorerType; awayTeam: HighScorerType},
  compPrivate: TCompPrivate,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log('Processing high scorer question', question);
  const getScore = (player: TMatchPlayer) =>
    question.templateType === CompQuestionTemplateType.AVERAGE_HIGH_SCORER
      ? player.averageScore
      : player.lastScore;
  const getHighScorer = (players: TMatchPlayer[]) => {
    if (players.length === 0) {
      return undefined;
    }
    return players.reduce((prev, curr) => {
      return (getScore(prev) ?? 0) > (getScore(curr) ?? 0) ? prev : curr;
    });
  };

  const homeHighScorer = getHighScorer(tokens.homeTeam?.players ?? []);
  const awayHighScorer = getHighScorer(tokens.awayTeam?.players ?? []);
  if (
    !homeHighScorer ||
    !awayHighScorer ||
    (question.templateType === CompQuestionTemplateType.AVERAGE_HIGH_SCORER &&
      homeHighScorer.averageScore === undefined &&
      awayHighScorer.averageScore === undefined) ||
    (question.templateType === CompQuestionTemplateType.LAST_HIGH_SCORER &&
      homeHighScorer.lastScore === undefined &&
      awayHighScorer.lastScore === undefined)
  ) {
    throw new Error(
      `Unable to determine the high scorer for both teams on a '${question.templateType}' question`,
    );
  }

  const {compPrivate: updatedCompPrivate} =
    populateLiveScoreConfigurationPlayers(question, tokens, compPrivate);
  question = {
    ...question,
    answers: [
      {
        label: `${homeHighScorer.name} ${tokens.homeTeam.code} ${bracketedScores(homeHighScorer)}`,
        autoResolveId: homeHighScorer.id,
      },
      {
        label: `${awayHighScorer.name} ${tokens.awayTeam.code} ${bracketedScores(awayHighScorer)}`,
        autoResolveId: awayHighScorer.id,
      },
    ],
  };
  return {question, compPrivate: updatedCompPrivate};
};

type TeamAboveAverageType = Pick<TMatch['homeTeam'], 'code' | 'players'>;
export const processAboveAverageScorersQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: {homeTeam: TeamAboveAverageType; awayTeam: TeamAboveAverageType},
  compPrivate: TCompPrivate,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log('Processing above average scorers question', question);
  const getPlayers = (players: TMatchPlayer[]) =>
    players
      .sort((a, b) => (b.averageScore ?? 0) - (a.averageScore ?? 0))
      .slice(0, 7);
  const isHome =
    question.templateType ===
    CompQuestionTemplateType.HOME_ABOVE_AVERAGE_SCORERS;
  const players = getPlayers(
    isHome ? tokens.homeTeam?.players ?? [] : tokens.awayTeam?.players ?? [],
  );
  if (players.length < 3) {
    throw new Error(
      `At least 3 players are required for a '${question.templateType}' question`,
    );
  }

  const {compPrivate: updatedCompPrivate} =
    populateLiveScoreConfigurationPlayers(question, tokens, compPrivate);
  question = {
    ...question,
    answers: players.map((p) => ({
      label: `${p.name} ${bracketedScores(p)}`,
      autoResolveId: p.id,
    })),
  };
  return {question, compPrivate: updatedCompPrivate};
};

export const processFanMvpQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: {homeTeam: TeamPlayersType; awayTeam: TeamPlayersType},
  compPrivate: TCompPrivate,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log('Processing fan mvp question', question);
  question.answers = [];
  populateLiveScoreConfigurationPlayers(question, tokens, compPrivate);
  return {question, compPrivate};
};

type TeamPlayersType = Pick<TMatch['homeTeam'], 'players'>;
const populateLiveScoreConfigurationPlayers = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: {homeTeam: TeamPlayersType; awayTeam: TeamPlayersType},
  compPrivate: TCompPrivate,
): {question: TCompQuestion; compPrivate: TCompPrivate} => {
  if (!compPrivate.liveScoreConfiguration) {
    throw new Error(
      `A liveScoreConfiguration is required on TCompPrivate to configure a '${question.templateType}' question`,
    );
  }

  const mapPlayers = (
    players: TMatchPlayer[],
    getScore: (player: TMatchPlayer) => number | undefined,
  ) =>
    players.flatMap((p) =>
      getScore(p) !== undefined
        ? [
            assertType<TLiveScorePlayer>({
              playerId: p.id,
              score: getScore(p)!,
              name: p.name,
            }),
          ]
        : [],
    );
  const getAverageScore = (player: TMatchPlayer) => player.averageScore ?? 0;
  const getLastScore = (player: TMatchPlayer) => player.lastScore ?? 0;
  compPrivate = {
    ...compPrivate,
    liveScoreConfiguration: {
      ...compPrivate.liveScoreConfiguration,
      homeAveragePlayers: mapPlayers(
        tokens.homeTeam?.players ?? [],
        getAverageScore,
      ),
      awayAveragePlayers: mapPlayers(
        tokens.awayTeam?.players ?? [],
        getAverageScore,
      ),
      homeLastPlayers: mapPlayers(tokens.homeTeam?.players ?? [], getLastScore),
      awayLastPlayers: mapPlayers(tokens.awayTeam?.players ?? [], getLastScore),
    },
  };
  return {question, compPrivate};
};

const TRY_SCORER_PLAYER_COUNT = 8;

// Maps NRL KB folder names (comp.teamId) to NRL team codes (tokens.homeTeam.code)
export const NRL_TEAM_ID_TO_CODE: Record<string, string> = {
  'nzwarriors': 'WAR',
  'north-queensland-cowboys': 'NQU',
  'canterbury-bulldogs': 'CBB',
  'wests-tigers': 'WTG',
  'manly-sea-eagles': 'MAN',
  'melbourne-storm': 'MBS',
  'canberra-raiders': 'CBR',
  'brisbane-broncos': 'BRI',
  'newcastle-knights': 'NEW',
  'parramatta-eels': 'PAR',
  'penrith-panthers': 'PEN',
  'st-george-illawarra-dragons': 'SGI',
  'cronulla-sharks': 'CSS',
  'sydney-roosters': 'SYD',
  'south-sydney-rabbitohs': 'SSY',
  'gold-coast-titans': 'GCO',
  'dolphins': 'DOL',
};

const normaliseTeamName = (v: string): string =>
  v.toLowerCase().replace(/[^a-z0-9]+/g, '');

/**
 * Which side of the fixture the competition's team is on.
 *
 * The NRL code map is tried first because it is exact and covers all 17 clubs.
 * It contains only those clubs, though, so every NPC and international
 * competition fell through it to `undefined`, and the caller read that as "not
 * home" and silently listed the *opposition's* players. Both rugby games
 * audited on 22 August happened to have the tracked team away, so the lists
 * were right by luck.
 *
 * Returns undefined when neither side matches, so the caller can flag it rather
 * than guess — offering the wrong team's players is worse than no question.
 */
export const resolveTeamSide = (
  teamId: string | undefined,
  homeTeam: Pick<TMatch['homeTeam'], 'id' | 'name' | 'shortName' | 'code'>,
  awayTeam: Pick<TMatch['homeTeam'], 'id' | 'name' | 'shortName' | 'code'>,
  /**
   * Names the team document knows itself by. The id alone is not enough: the
   * All Blacks are `allblacks` to us and "New Zealand" to World Rugby, so slug
   * matching alone finds neither side. Mirrors getTeamSide server-side.
   */
  teamAliases: Array<string | undefined> = [],
): 'home' | 'away' | undefined => {
  if (!teamId) {
    return undefined;
  }

  const mappedCode = NRL_TEAM_ID_TO_CODE[teamId];
  if (mappedCode) {
    if (mappedCode === homeTeam.code) {
      return 'home';
    }
    if (mappedCode === awayTeam.code) {
      return 'away';
    }
  }

  const wanted = new Set(
    [teamId, ...teamAliases]
      .filter((v): v is string => !!v)
      .map(normaliseTeamName),
  );
  const matches = (t: Pick<TMatch['homeTeam'], 'id' | 'name' | 'shortName' | 'code'>) =>
    [t.id, t.name, t.shortName, t.code]
      .filter((v): v is string => !!v)
      .some((v) => wanted.has(normaliseTeamName(v)));

  if (matches(homeTeam)) {
    return 'home';
  }
  if (matches(awayTeam)) {
    return 'away';
  }
  return undefined;
};

/** The names a team document knows itself by, for resolveTeamSide. */
export const fetchTeamAliases = async (
  teamId: string | undefined,
): Promise<string[]> => {
  if (!teamId) {
    return [];
  }
  try {
    const doc = await firebase.firestore().collection('teams').doc(teamId).get();
    const data = doc.data() as
      | {name?: string; shortName?: string; code?: string}
      | undefined;
    if (!data) {
      return [];
    }
    return [data.name, data.shortName, data.code].filter(
      (v): v is string => !!v,
    );
  } catch {
    // Falling back to the id alone still works for NRL clubs via the code map.
    return [];
  }
};

/** Catch-all label for a try-scorer list, named for the team it belongs to. */
export const anyOtherPlayerLabel = (
  team: Pick<TMatch['homeTeam'], 'shortName' | 'name'> | undefined,
): string => {
  const noun = team?.shortName || team?.name;
  return noun ? `Any other ${noun} player` : 'Any other player';
};

/**
 * Order a squad by how likely each player is to score.
 *
 * The bookmaker's anytime try scorer market first, shortest price leading. It
 * is the only source that actually reflects who scores tries, and it is what
 * the market and the fanbase already believe.
 *
 * Scoring average is the fallback, but only NRL players have one — everyone on
 * an NPC or international team sheet carries zero, which made the old sort a
 * no-op and quietly reduced the shortlist to the first eight names on the
 * sheet, i.e. the forward pack. For Lions v New Zealand that would have dropped
 * six of the market's top eight including both favourites.
 *
 * Players the market has not priced sort last rather than being dropped: a
 * squad is still a valid list when the market is not posted yet.
 */
/**
 * How likely a position is to score, lower being more likely.
 *
 * Only a last resort, for the case where the market is not posted and nobody
 * carries a scoring average — which is every football fixture, because ESPN
 * publishes no per-player season average. Football squads arrive ordered
 * goalkeepers first, so without this the shortlist for "pick a Gunner to score"
 * was four goalkeepers and four defenders.
 *
 * Unknown positions all return the same middle weight, so rugby team sheets —
 * which use names like "Fullback" and "Lock" — sort exactly as they did before.
 */
const POSITION_SCORING_RANK: Record<string, number> = {
  // Forwards
  F: 0, CF: 0, ST: 0, S: 0, SS: 0,
  // Wingers and attacking midfielders
  AM: 1, W: 1, LW: 1, RW: 1,
  // Midfielders
  M: 2, CM: 2, DM: 2, LM: 2, RM: 2,
  // Defenders
  D: 4, CD: 4, CB: 4, LB: 4, RB: 4, LWB: 4, RWB: 4,
  // Goalkeeper
  G: 5, GK: 5,
};

/** Neutral, so an unrecognised vocabulary changes nothing about the order. */
const UNKNOWN_POSITION_RANK = 3;

const positionScoringRank = (position: string | undefined): number => {
  if (!position) {
    return UNKNOWN_POSITION_RANK;
  }
  // ESPN uses plain families on a season roster ("D") and qualified ones in a
  // match summary ("CD-L", "AM-R"); the part before the qualifier is the family.
  const family = position.trim().toUpperCase().split(/[^A-Z]/)[0];
  return POSITION_SCORING_RANK[family] ?? UNKNOWN_POSITION_RANK;
};

export const rankTryScorers = (players: TMatchPlayer[]): TMatchPlayer[] =>
  players
    .filter((p) => p.position !== 'Reserve')
    .slice()
    .sort((a, b) => {
      const aOdds = a.tryScorerOdds;
      const bOdds = b.tryScorerOdds;
      if (aOdds !== undefined && bOdds !== undefined) {
        return aOdds - bOdds;
      }
      if (aOdds !== undefined) {
        return -1;
      }
      if (bOdds !== undefined) {
        return 1;
      }
      const byAverage = (b.averageScore ?? 0) - (a.averageScore ?? 0);
      if (byAverage !== 0) {
        return byAverage;
      }
      return positionScoringRank(a.position) - positionScoringRank(b.position);
    });

type TryScorerType = Pick<TMatch['homeTeam'], 'players' | 'shortName' | 'name'>;
export const processTryScorerQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: {homeTeam: TryScorerType; awayTeam: TryScorerType},
  compPrivate: TCompPrivate,
  teamIsHome?: boolean,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log('Processing try scorer question', question);
  let team: TryScorerType | undefined;
  if (question.templateType === CompQuestionTemplateType.HOME_TRY_SCORER) {
    team = tokens.homeTeam;
  } else if (
    question.templateType === CompQuestionTemplateType.AWAY_TRY_SCORER
  ) {
    team = tokens.awayTeam;
  } else {
    // TEAM_TRY_SCORER / TEAM_GOAL_SCORER — side resolved from comp.teamId by
    // the caller. The two differ only in which stat grades them at full time;
    // building the answer list is identical.
    team = teamIsHome === true ? tokens.homeTeam : tokens.awayTeam;
  }
  const teamPlayers: TMatchPlayer[] = team?.players ?? [];
  const sortedPlayers = rankTryScorers(teamPlayers).slice(
    0,
    TRY_SCORER_PLAYER_COUNT,
  );

  if (sortedPlayers.length < 3) {
    throw new Error(
      `At least 3 players are required for a '${question.templateType}' question`,
    );
  }

  const {compPrivate: updatedCompPrivate} =
    populateLiveScoreConfigurationPlayers(question, tokens, compPrivate);
  question = {
    ...question,
    answers: [
      ...sortedPlayers.map((p) => ({
        // Trimmed because bracketedScores is empty for any player without a
        // score — which is everyone on an NPC or international team sheet, and
        // is why every one of those labels carried a trailing space.
        //
        // Market odds order the list but deliberately do not appear in it:
        // a price on a player's name reads as a betting product, and a
        // fractional price means nothing to someone picking a try scorer.
        label: `${p.name} ${bracketedScores(p)}`.trim(),
        // TAB-sourced synthetic players have a "tab:" prefix and no real ID yet.
        // Resolution falls back to name matching in CompetitionUtils.
        autoResolveId: p.id && !p.id.startsWith('tab:') ? p.id : undefined,
      })),
      {label: anyOtherPlayerLabel(team)},
    ],
  };
  return {question, compPrivate: updatedCompPrivate};
};

type TeamHighScorerType = Pick<TMatch['homeTeam'], 'players'>;
export const processTeamHighScorerQuestion = <
  Q extends TCompQuestion | TFirebaseQuestion,
>(
  question: Q,
  tokens: {homeTeam: TeamHighScorerType; awayTeam: TeamHighScorerType},
  compPrivate: TCompPrivate,
): {question: Q; compPrivate: TCompPrivate} => {
  console.log('Processing team high scorer question', question);
  const getPlayers = (players: TMatchPlayer[]) => {
    const sortedPlayers = players.sort((a, b) => {
      if ((a.lastScore ?? 0) === (b.lastScore ?? 0)) {
        return (b.averageScore ?? 0) - (a.averageScore ?? 0);
      }
      return (b.lastScore ?? 0) - (a.lastScore ?? 0);
    });
    return sortedPlayers.slice(0, 3);
  };
  const isHome =
    question.templateType === CompQuestionTemplateType.HOME_HIGH_SCORER;
  const players = getPlayers(
    isHome ? tokens.homeTeam?.players ?? [] : tokens.awayTeam?.players ?? [],
  );
  if (players.length < 3) {
    throw new Error(
      `At least 3 players are required for a '${question.templateType}' question`,
    );
  }

  const {compPrivate: updatedCompPrivate} =
    populateLiveScoreConfigurationPlayers(question, tokens, compPrivate);
  question = {
    ...question,
    answers: [
      ...players.map((p) => ({
        label: `${p.name} ${bracketedScores(p)}`,
        autoResolveId: p.id,
      })),
      {label: 'Any other player'},
    ],
  };
  return {question, compPrivate: updatedCompPrivate};
};
